import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  IconButton,
  Box,
  Typography,
  Link as MuiLink,
  useMediaQuery,
} from '@mui/material'
import {
  Person as PersonIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { ContractStatusBadge } from '../contracts/ContractStatusBadge'
import type { GroupMember } from '@/types'
import { format } from 'date-fns'
import { Link as RouterLink } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'

interface GroupMembersProps {
  members: GroupMember[]
  onRemoveMember: (studentId: number) => Promise<void>
  isLoading?: boolean
  isTeacher?: boolean
}

export function GroupMembers({
  members,
  onRemoveMember,
  isLoading,
  isTeacher,
}: GroupMembersProps) {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const getUserDisplayName = (student: GroupMember['student']) => {
    if (!student) {
      return '-'
    }
    if (student.first_name && student.surname) {
      return `${student.surname} ${student.first_name}`
    }
    return student.email
  }

  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {members.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {t('groups.noMembers')}
            </Typography>
          </Box>
        ) : (
          members.map((member) => (
            <Paper key={member.contract_id} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ width: 32, height: 32 }}>
                  <PersonIcon fontSize="small" />
                </Avatar>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  {member.student ? (
                    <MuiLink
                      component={RouterLink}
                      to={`/users/${member.student.id}`}
                      underline="hover"
                      sx={{ fontSize: 14, fontWeight: 700, display: 'block' }}
                    >
                      {getUserDisplayName(member.student)}
                    </MuiLink>
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {getUserDisplayName(member.student)}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {member.student?.email || '-'}
                  </Typography>
                </Box>
                {isTeacher && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      if (!member.student) return
                      onRemoveMember(member.student.id)
                    }}
                    disabled={isLoading || !member.student}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Box
                sx={{
                  mt: 1.5,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 1,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('contracts.status')}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <ContractStatusBadge status={member.status} />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('groups.memberSince')}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {format(new Date(member.starts_at), 'dd.MM.yyyy')}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          ))
        )}
      </Box>
    )
  }

  if (members.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          {t('groups.noMembers')}
        </Typography>
      </Box>
    )
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t('groups.member')}</TableCell>
            <TableCell>{t('common.email')}</TableCell>
            <TableCell>{t('contracts.status')}</TableCell>
            <TableCell>{t('groups.memberSince')}</TableCell>
            {isTeacher && <TableCell>{t('common.actions')}</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {members.map((member) => (
            <TableRow key={member.contract_id}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 32, height: 32 }}>
                    <PersonIcon fontSize="small" />
                  </Avatar>
                  {member.student ? (
                    <MuiLink
                      component={RouterLink}
                      to={`/users/${member.student.id}`}
                      underline="hover"
                      sx={{ fontSize: 14, fontWeight: 600 }}
                    >
                      {getUserDisplayName(member.student)}
                    </MuiLink>
                  ) : (
                    getUserDisplayName(member.student)
                  )}
                </Box>
              </TableCell>
              <TableCell>{member.student?.email || '-'}</TableCell>
              <TableCell>
                <ContractStatusBadge status={member.status} />
              </TableCell>
              <TableCell>
                {format(new Date(member.starts_at), 'dd.MM.yyyy')}
              </TableCell>
              {isTeacher && (
                <TableCell>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      if (!member.student) return
                      onRemoveMember(member.student.id)
                    }}
                    disabled={isLoading || !member.student}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
